import React from "react";
import { useSelect } from "downshift";
import { VisuallyHidden } from "../styled/VisuallyHidden";
import styled from "styled-components";
import Button from "../styled/Button";

export type FilterSelectItem = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

const MobileBreakPoints = ["base", "mobile"];

const Container = styled.div<{ bgColor: string; color: string }>`
  position: relative;
  height: 100%;
  --fs-color: ${({ color }) => color};
  --fs-bg-color: ${({ bgColor }) => bgColor};
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, `h4Heading`)};
        width: ${props.theme.spacePx(breakpoint, 5)};
      `;
    })}

  &::after {
    display: block;
    content: "";
    position: absolute;
    background-color: #000;
    height: 1px;
    width: 100%;
    top: 0px;
    left: 0px;
    z-index: 3;
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        &::after {
          top: ${(
            -2 +
            props.theme.space(
              breakpoint,
              MobileBreakPoints.includes(breakpoint) ? 6 : 7
            )
          ).toFixed(0)}px;
        }
      `;
    })}
`;

const ChoiceButton = styled(Button)`
  display: block;
  height: 100%;
  background-color: var(--fs-bg-color, #fff);
  position: absolute;
  z-index: 4;
  & span {
    display: block;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  color: var(--fs-color, #000);
  padding-top: 0.1em;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, `h4Heading`)};
        width: ${props.theme.spacePx(breakpoint, 5)};
      `;
    })}
`;

const Options = styled.ul<{ isOpen: boolean; bgColor: string; color: string }>`
  position: absolute;
  top: 0;
  left: -1px;
  background-color: var(--fs-bg-color, #fff);
  z-index: 2;
  margin: 0;
  padding: 0;
  overflow: hidden;
  overflow-y: ${({ isOpen }) => (isOpen ? "auto" : "hidden")};
  overflow-y: ${({ isOpen }) => (isOpen ? "overlay" : "hidden")};

  scrollbar-width: thin;
  scrollbar-color: var(--fs-color, #000) white;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: auto;

  border: ${({ isOpen }) => (isOpen ? "1px solid #000" : "none")};
  border-top: none;

  height: ${({ isOpen }) => (isOpen ? "auto" : 0)};

  outline: none;

  &::-webkit-scrollbar {
    width: 11px;
    position: absolute;
    right: 0;
  }

  &::-webkit-scrollbar-track {
    border-left: 5px solid var(--fs-bg-color, #fff);
    border-right: 5px solid var(--fs-bg-color, #fff);
    background-color: var(--fs-color, #000);
    border-radius: 0;
    margin: 5px 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: var(--fs-color, #000);
    border-left: 3px solid var(--fs-bg-color, #fff);
    border-right: 3px solid var(--fs-bg-color, #fff);
    border-radius: 0;
    cursor: pointer;
    display: block;
  }

  @media (any-pointer: fine) {
    &:hover {
      &::-webkit-scrollbar-thumb {
        border-left: 2px solid var(--fs-bg-color, #fff);
        border-right: 2px solid var(--fs-bg-color, #fff);
      }
    }
  }

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        max-height: ${(
          props.theme.space(
            breakpoint,
            MobileBreakPoints.includes(breakpoint) ? 7 : 8
          ) * 7.5
        ).toFixed(2)}px;
        padding: ${
          props.isOpen
            ? `${(2 * props.theme.fontValign(breakpoint, "h4Heading")).toFixed(
                2
              )}px`
            : "0"
        } 0;
        
        width: ${(2 + props.theme.space(breakpoint, 5)).toFixed(2)}px;
        top: ${(
          -1 +
          props.theme.space(
            breakpoint,
            MobileBreakPoints.includes(breakpoint) ? 6 : 7
          )
        ).toFixed(2)}px;
      `;
    })}

  &:focus-visible {
    outline: none;
  }
`;

const Option = styled.li`
  background-color: var(--fs-bg-color, #fff);
  color: var(--fs-color, #000);
  margin: 0;
  padding: 0;
  list-style: none;
  width: 100%;
  display: block;
  text-align: center;
  cursor: pointer;
  &[disabled] {
    color: #999;
  }
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        ${props.theme.textStyle(breakpoint, `h4Heading`)};
        font-weight: 400;
        ${
          MobileBreakPoints.includes(breakpoint)
            ? `
          line-height: ${props.theme.spacePx(breakpoint, 6)};
        `
            : ""
        }
      `;
    })}
`;

export const FilterSelect = ({
  label,
  labelSelect,
  items,
  selected,
  onChange,
  colorScheme = "white",
}: {
  label: string;
  colorScheme?: string;
  labelSelect?: string;
  items: FilterSelectItem[];
  selected: string | number | undefined | null;
  onChange: (item: FilterSelectItem | null | undefined) => void;
}) => {
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect({
    items,
    // this is now a controlled component ...
    selectedItem: items.find((item) => item?.value === selected),
    itemToString: (item: FilterSelectItem | null) => {
      return item ? item.label : "";
    },
    onSelectedItemChange: (changes) => {
      if (typeof onChange === "function")
        onChange.call(null, changes.selectedItem);
    },
  });

  return (
    <Container
      color={colorScheme === "black" ? "#fff" : "#000"}
      bgColor={colorScheme === "black" ? "#000" : "#fff"}
    >
      <VisuallyHidden>
        <label {...getLabelProps()}>{label}</label>
      </VisuallyHidden>

      <ChoiceButton type="button" {...getToggleButtonProps()}>
        <span>{selectedItem?.label ?? labelSelect ?? "Choose"}</span>
      </ChoiceButton>
      <Options {...getMenuProps()} isOpen={isOpen}>
        {isOpen &&
          items.map((item, index) => (
            <Option
              style={
                highlightedIndex === index || items?.[index]?.value === selected
                  ? { fontWeight: 700 }
                  : {}
              }
              key={`${item}${index}`}
              {...getItemProps({ item, index, disabled: item.disabled })}
            >
              {item.label}
            </Option>
          ))}
      </Options>
    </Container>
  );
};

export default FilterSelect;
